import { DesignCategory, MeshVerticesCount, DesignType, InsoleDesign } from "models/InsoleDesign";
import { Insole, PressureImage, resolveInsoleTemplateApiName } from "models/Insole";
import { Addition, AdditionNewStatus, AutomaticAdditions } from "models/Addition";
import { loadFromArrayBuffer } from "Utils/ViewerUtils";
import { MeshStandardMaterial, Mesh } from "three";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import * as THREE from "three";
import { useDesignStore } from "context/DesignContext";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import produce from "immer";
import PressureData from "Scan/PressureData";
import { Patient } from "models/Patient";
import { AutoProperties, AutoPropertiesDTO } from "models/AutoProperties";
import { SaveSettings } from "components/Viewer3d/SaveManager";
import { PatientConsditions } from "models/PatientConditions";
import { Engraving, EngravingResponseDto } from "models/Engraving";
import { DesignPressureAlignmentRequestDto, GetDesignPressureResponseDto } from "models/DesignPressureAlignment";
import Calibrator from "Scan/Calibrator";
import JSZip from "jszip";
import { HardnessValueMap } from "components/Viewer3d/consts";
import { additionNameGroup } from "components/PropertyPanel/PropertyPanel";
import { RONDOM_PRESSUURE_PIXEL_WIDTH } from "components/Viewer3d/RondomConsts";
import { GCodeSetting, getDefaultGcodeSetting } from "models/GcodeSetting";
import { PaginatedSearchRequestDto } from "models/PaginatedSearchRequestDto";
import { PaginatedResponseDto } from "models/PaginatedResponseDto";
import { GetBones } from "./WBCTService";

export async function fetchDesignDetail(api: any, designId: number) {
  try {
    const response = await api.get(`/api/designs/${designId}/detail`);
    if (!response.ok) throw new Error("Failed to fetch design detail");
    return response.body;
  } catch (error) {
    return null;
  }
}

export async function getNewAddition(api: any, additionName: string, side: "right" | "left"): Promise<Addition | null> {
  const res = await api.get(`/api/additions/byname?additionName=${additionName}`);
  if (res.ok) {
    const addi = new Addition();
    addi.loadFromJson(res.body, useDesignStore.getState().insoleDesign?.category);
    addi.foot = side;
    const additionMeshResponse = await api.get(`/api/additions/${addi.id}/mesh`);
    if (additionMeshResponse.body) {
      const geometry = loadFromArrayBuffer(additionMeshResponse.body);
      if (geometry) {
        addi.loadAdditionFromBufferGeo(geometry);
        return addi;
      }
    }
  }
  return null;

}

export async function loadInsole(api: any, designId: number, footSide: "right" | "left", responseBody: any, designCategory: DesignCategory): Promise<Insole | null> {
  const insole = new Insole();
  insole.side = footSide;
  try {
    const arrayBuffer = await api.get(`/api/designs/${designId}/meshes/${footSide}`);
    if (!arrayBuffer?.body) return null;
    const isValid = insole.load2(arrayBuffer.body);
    const vertexCount = insole.lowpolyBufferGeometry?.attributes.position.count;
    const vertexLimit = MeshVerticesCount[designCategory];
    if (!isValid || vertexCount !== vertexLimit) {
      console.error('number of mesh vertices is not correct');
      return null;
    }
  } catch (error) {
    console.error(`load Insole ${error}`);
    return null;
  }

  try {
    const additionList = responseBody.additions.filter((item: any) => String(item.footSide).toLowerCase() === footSide);
    for (const item of additionList) {
      try {
        const additionMeshResponse = await api.get(`/api/additions/${item.id}/mesh`);
        if (!additionMeshResponse?.body) {
          console.error(`Empty addition mesh body for addition ID ${item.id}`);
          return null;
        }
        const addition = new Addition();
        addition.loadFromJson(item, designCategory);
        const geometry = loadFromArrayBuffer(additionMeshResponse.body);
        if (!geometry) {
          console.error(`Couldn’t load geometry for addition ID ${item.id}`);
          return null;
        }
        addition.loadAdditionFromBufferGeo(geometry);
        insole.additions.push(addition);
      } catch (innerErr) {
        console.error(`Failed to load addition (ID: ${item.id}) for ${footSide}:`, innerErr);
      }
    }
  } catch (error) {
    console.error(`Failed to process additions for ${footSide}:`, error);
  }

  return insole;
}

const base64ToImageData = (base64String: string): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    try {
      const cleanBase64 = base64String.includes(',')
        ? base64String.split(',')[1]
        : base64String;

      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0);
        resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
      };

      img.onerror = reject;
      img.src = `data:image/jpeg;base64,${cleanBase64}`;
    } catch (err) {
      reject(err);
    }
  });
};

export async function loadRondomImage(api: any, designId: number, designType: DesignType = DesignType.General) {
  const hasRondom = useSelectedDesignStore.getState().hasRondomImage;

  if (!hasRondom) {
    useDesignStore.setState({ RondomImageData: [undefined, undefined] });
    return;
  }

  try {
    const res = await api.get(`/api/designs/${designId}/rondomImage`);
    if (res.ok) {
      const rightImageData = await base64ToImageData(res.body.rightData);
      //debugDownloadImageData(rightImageData,'test.jpeg');
      const leftImageData = await base64ToImageData(res.body.leftData);
      const rightPressureImage: PressureImage = {
        image: rightImageData,
        realHeight: rightImageData.height * RONDOM_PRESSUURE_PIXEL_WIDTH,
        realWidth: rightImageData.width * RONDOM_PRESSUURE_PIXEL_WIDTH
      };

      const leftPressureImage: PressureImage = {
        image: leftImageData,
        realHeight: leftImageData.height * RONDOM_PRESSUURE_PIXEL_WIDTH,
        realWidth: leftImageData.width * RONDOM_PRESSUURE_PIXEL_WIDTH
      };
      const allImages: PressureImage[] = [];
      allImages.push(rightPressureImage);
      allImages.push(leftPressureImage);

      useDesignStore.setState({ RondomImageData: allImages });
      useSelectedDesignStore.setState({ hasRondomImage: true });
    } else {
      console.error(`error loading null pressure data`);
      return;
    }
  } catch (error) {
    console.error(`error loading pressure data: ${error}`);
    return;
  }

  // // Load alignment to store
  const designPressureDto = await getDesignPressureAlignments(api, designId);
  if (designPressureDto == null) return null;
  useDesignStore.setState({ pressureAlignment: designPressureDto?.toPressureAlignment() });
}

export async function loadPressure(api: any, designId: number, designType: DesignType = DesignType.General): Promise<PressureData | null> {
  const hasPressure = useSelectedDesignStore.getState().hasPressure;

  if (designType === DesignType.Template && hasPressure) {
    return useDesignStore.getState().insoleDesign?.pressureData!;
  }

  // Direct-open flows (including the exhibition showcase) do not pass through
  // DatabasePanel, so the local hasPressure flag may still be false. Treat the
  // backend pressure endpoint as the source of truth before deciding to skip it.
  const designPressureDto = await getDesignPressureAlignments(api, designId);
  if (designPressureDto == null) {
    useSelectedDesignStore.setState({ hasPressure: false });
    useDesignStore.setState({ pressureImageData: [undefined, undefined] });
    useDesignStore.getState().setPressureAlignment(null);
    return null;
  }
  useDesignStore.getState().setPressureAlignment(designPressureDto?.toPressureAlignment());

  // Load file and return it
  const pressureData = new PressureData();
  try {
    const result = await pressureData.Load();
    if (result) {
      useSelectedDesignStore.setState({ hasPressure: true });
      return pressureData;
    } else {
      useSelectedDesignStore.setState({ hasPressure: false });
      console.error(`error loading null pressure data`);
      return null;
    }
  } catch (error) {
    useSelectedDesignStore.setState({ hasPressure: false });
    console.error(`error loading pressure data: ${error}`);
    return null;
  }
}

function loadAutos(res: any) {
  const autoPr = new AutoProperties();
  if (res) {
    const dto = AutoPropertiesDTO.fromJson(res);
    autoPr.loadFromDTO(dto);
  }
  return autoPr;
}

function loadPatientAndConditions(res: any, designType: DesignType): Patient | null {

  if (designType === DesignType.Template) return useDesignStore.getState().patient;

  let patient: Patient | null = new Patient();
  const patientConditoins = new PatientConsditions();
  if (res.patient) {
    const dto = AutoPropertiesDTO.fromJson(res.autoProperties);
    patientConditoins.loadFromDTO(dto);
    patient = Patient.fromJson(res.patient);
    patient.patientConditions = patientConditoins;
  } else {
    patient = null;
  }
  return patient;
}

function loadEngraving(res: any, designName: string) {
  let engravingModel: Engraving;
  const UIDString = useSelectedDesignStore.getState().designId.toString().padStart(6, '0');
  if (res != null) {
    const engravingDto = new EngravingResponseDto(res);
    engravingModel = engravingDto.toEngravingModel({ UIDString: UIDString, textString: designName });
  } else {
    engravingModel = new Engraving({ UIDString: UIDString, textString: designName })
  }
  useDesignStore.getState().setEngraving(engravingModel);
}

export async function loadDesign(api: any, designId: number): Promise<[InsoleDesign | null, Patient | null]> {
  const design = new InsoleDesign();
  console.groupCollapsed(`Loading Design ID: ${designId}`);

  const responseBody = await fetchDesignDetail(api, designId);
  if (!responseBody) console.log("Empty design detail response.")

  design.loadFromJson(responseBody);
  const [insoleRight, insoleLeft] = await Promise.all([
    loadInsole(api, designId, "right", responseBody, design.category),
    loadInsole(api, designId, "left", responseBody, design.category),
  ]);
  if (!insoleRight || !insoleLeft) {
    console.error("One or both insoles failed to load.");
    return [null, null];
  }

  design.insoles = [insoleRight, insoleLeft];


  if (design.designType === DesignType.Template) {
    loadPressureFromPreviousDesign(design);
    loadWBCTFromPreviousDesign(design);
  } else if (design.designType === DesignType.General) {
    design.pressureData = await loadPressure(api, designId, design.designType);
    await loadRondomImage(api, designId, design.designType);
    await GetBones(api, "All", designId, design);
  }

  design.autoProperties = loadAutos(responseBody.autoProperties);
  const patient = loadPatientAndConditions(responseBody, design.designType);

  if (design.designType != DesignType.Template) {
    loadEngraving(responseBody.engraving, design.name);
    await GetSavedGcodeSetting(api, designId).then((gSetting) => {
      if (gSetting != null) {
        useDesignStore.setState({ gcodeSetting: gSetting });
      }
    });
  }

  console.log("🎯 Design loaded successfully!");
  console.groupEnd();

  return [design, patient];
}

async function saveAutoDesignParameters(api: any, design: InsoleDesign, designId: number) {
  const dto = design.autoProperties?.saveToDto();
  const jsonData = dto?.saveToJson();
  const payload = {
    "name": design.name,
    "autoProperties": jsonData
  };
  const resDesing = await api.patch(`/api/designs/${designId}`, payload);
  if (!resDesing.ok) return;
}

export async function saveDesign(api: any, design: InsoleDesign, designId: number, settings: SaveSettings) {
  //save design infos
  const designJson = {
    'name': design?.name,
    'category': design?.category
  };
  if (settings.updateDesignInfo || settings.updateDesignCategory) {
    const resDesing = await api.patch(`/api/designs/${designId}`, designJson);
    if (!resDesing.ok) return;
    useSelectedDesignStore.setState({ designUpdated: true });
  }

  ///save meshes
  var response = null;
  if (settings.updateInsoleMesh) {
    const exporter = new STLExporter();
    const standardMaterial = {
      color: 0x156289,
      emissive: 0x072534,
      side: THREE.DoubleSide,
      flatShading: false,
      metalness: 0.8,
      roughness: 0.1,
      opacity: 1.0,
      transparent: false,
    };
    const leftMesh = new Mesh(design.insoles[1].lowpolyBufferGeometry ?? undefined, new MeshStandardMaterial(standardMaterial));
    const rightMesh = new Mesh(design.insoles[0].lowpolyBufferGeometry ?? undefined, new MeshStandardMaterial(standardMaterial));
    const leftArrayBuffer = exporter.parse(leftMesh, { binary: true }).buffer;
    const rightArrayBuffer = exporter.parse(rightMesh, { binary: true }).buffer;

    const formData = new FormData();
    formData.append("LeftFootStl", new Blob([leftArrayBuffer as ArrayBuffer], { type: "application/vnd.ms-pki.stl" }), "left.stl");
    formData.append("RightFootStl", new Blob([rightArrayBuffer as ArrayBuffer], { type: "application/vnd.ms-pki.stl" }), "right.stl");

    response = await api.post(`/api/designs/${designId}/bmeshes`, formData);
    if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);
    //////////////////
  }

  // save or update additions
  if (settings.newAddition) {
    await saveNewAdditions(api, design, designId);
  }
  if (settings.updateAddition) {
    await updateAdditons(api, design, designId);
  }

  if (settings.replaceAdditons) {
    await replaceAdditons(api, design, designId);
    saveAutoDesignAdditions(api, design, designId);
  }

  if (settings.removeAddition) {
    await removeAdditions(api, design, designId);
    saveAutoDesignAdditions(api, design, designId);
  }

  //wait for addition id to set
  if (settings.updateAutoProperties)
    saveAutoDesignAdditions(api, design, designId);


  return response;
}

export async function updateAdditons(api: any, design: InsoleDesign, designId: number) {
  design.insoles.forEach((insole: Insole) => {
    insole.additions.forEach(async (addition) => {
      if (addition.isUpdated === true) {
        addition.isUpdated = false;
        const jsonBody = {
          "height": Math.round(addition.height.value),
          "location": {
            "x": addition.meshPosition.x,
            "y": addition.meshPosition.y,
            "z": addition.meshPosition.z,
          },
          "scale": {
            "x": addition.scale.x,
            "y": addition.scale.y,
            "z": addition.scale.z,
          },
          "angle": addition.angle.toFixed(3),
          "hardness": HardnessValueMap[addition.hardness]
        };
        await api.patch(`/api/designs/${designId}/designaddition/${addition.designAdditionId}`, jsonBody);
      }
      //setAllAdditionsUpdatedFalse();
    });
  });
}

export async function removeAdditions(api: any, design: InsoleDesign, designId: number) {
  var toRemoveAdditonIndexes: number[] = [];
  design.insoles.forEach((insole: Insole) => {
    insole.additions.forEach(async (addition, index) => {
      if (addition.status === AdditionNewStatus.removed) {
        addition.status = AdditionNewStatus.none;
        toRemoveAdditonIndexes.push(addition.designAdditionId);
        useDesignStore.getState().removeAdditionFromDesign(addition.foot === 'right' ? 0 : 1, addition.designAdditionId);
      }
    });
  });

  toRemoveAdditonIndexes.forEach(element => {
    api.delete(`/api/designs/${designId}/additions/${element}`);
  });
}

export const setAllAdditionsUpdatedFalse = () => {
  useDesignStore.setState(
    produce((state) => {
      if (!state.insoleDesign) return;
      state.insoleDesign.insoles.forEach((insole: Insole) => {
        insole.additions.forEach((addition: Addition) => {
          addition.isUpdated = false;
        });
      });
    })
  );
};

export async function saveAutoDesignAdditions(api: any, design: InsoleDesign, designId: number) {
  design.insoles.forEach((insole, side) => {
    insole.additions.forEach((addition, index) => {
      if (addition.automaticAddition !== AutomaticAdditions.None) {
        var type = addition.automaticAddition;
        if (side === 0 && design.autoProperties) {
          design.autoProperties.metatarSalBarRight = (type === AutomaticAdditions.MetatarsalBar || type === AutomaticAdditions.MetatarsalBarFromPescavus) ? addition.designAdditionId : design.autoProperties.metatarSalBarRight;
          design.autoProperties.metatarSalPadRight = (type === AutomaticAdditions.MetatarsalPad) ? addition.designAdditionId : design.autoProperties.metatarSalPadRight;
          design.autoProperties.dancersPadRight = (type === AutomaticAdditions.DancerPad) ? addition.designAdditionId : design.autoProperties.dancersPadRight;
          design.autoProperties.mortonsExtensionRight = (type === AutomaticAdditions.MortonExtention) ? addition.designAdditionId : design.autoProperties.mortonsExtensionRight;
          design.autoProperties.cutoutMet1Right = (type === AutomaticAdditions.CutoutMet1) ? addition.designAdditionId : design.autoProperties.cutoutMet1Right;
          design.autoProperties.cutoutMet2Right = (type === AutomaticAdditions.CutoutMet2) ? addition.designAdditionId : design.autoProperties.cutoutMet2Right;
          design.autoProperties.cutoutMet3Right = (type === AutomaticAdditions.CutoutMet3) ? addition.designAdditionId : design.autoProperties.cutoutMet3Right;
          design.autoProperties.cutoutMet4Right = (type === AutomaticAdditions.CutoutMet4) ? addition.designAdditionId : design.autoProperties.cutoutMet4Right;
          design.autoProperties.cutoutMet5Right = (type === AutomaticAdditions.CutoutMet5) ? addition.designAdditionId : design.autoProperties.cutoutMet5Right;
        } else if (design.autoProperties) {
          design.autoProperties.metatarSalBarLeft = (type === AutomaticAdditions.MetatarsalBar || type === AutomaticAdditions.MetatarsalBarFromPescavus) ? addition.designAdditionId : design.autoProperties.metatarSalBarLeft;
          design.autoProperties.metatarSalPadLeft = (type === AutomaticAdditions.MetatarsalPad) ? addition.designAdditionId : design.autoProperties.metatarSalPadLeft;
          design.autoProperties.dancersPadLeft = (type === AutomaticAdditions.DancerPad) ? addition.designAdditionId : design.autoProperties.dancersPadLeft;
          design.autoProperties.mortonsExtensionLeft = (type === AutomaticAdditions.MortonExtention) ? addition.designAdditionId : design.autoProperties.mortonsExtensionLeft;
          design.autoProperties.cutoutMet1Left = (type === AutomaticAdditions.CutoutMet1) ? addition.designAdditionId : design.autoProperties.cutoutMet1Left;
          design.autoProperties.cutoutMet2Left = (type === AutomaticAdditions.CutoutMet2) ? addition.designAdditionId : design.autoProperties.cutoutMet2Left;
          design.autoProperties.cutoutMet3Left = (type === AutomaticAdditions.CutoutMet3) ? addition.designAdditionId : design.autoProperties.cutoutMet3Left;
          design.autoProperties.cutoutMet4Left = (type === AutomaticAdditions.CutoutMet4) ? addition.designAdditionId : design.autoProperties.cutoutMet4Left;
          design.autoProperties.cutoutMet5Left = (type === AutomaticAdditions.CutoutMet5) ? addition.designAdditionId : design.autoProperties.cutoutMet5Left;
        }
      }
    });
  });

  await saveAutoDesignParameters(api, design, designId);
}

export async function saveNewAdditions(api: any, design: InsoleDesign, designId: number) {
  var jsonData: any[] = [];
  var newAdditonIndexes: number[] = [];
  var newAdditonFootIndexes: number[] = [];
  design.insoles.forEach((insole, side) => {
    insole.additions.forEach((addition, index) => {
      if (addition.status === AdditionNewStatus.isNew) {
        addition.status = AdditionNewStatus.none;
        jsonData.push(addition.toJSON());
        newAdditonIndexes.push(index);
        newAdditonFootIndexes.push(side);
      }
    });
  });
  if (jsonData.length === 0) return;
  for (let index = 0; index < jsonData.length; index++) {
    const response = await api.post(`/api/designs/${designId}/additions`, jsonData[index]);
    if (response.ok) {
      const id = parseInt(response.body.designAdditionId);
      design.insoles[newAdditonFootIndexes[index]].additions[newAdditonIndexes[index]].designAdditionId = id;
    }
  }
}

export async function getNewDesign(api: any, templateName: string, preferredName?: string): Promise<number> {
  const jsonBody = {
    templateName: templateName,
    preferredName: preferredName?.trim() || "New Design"
  };
  const response = await api.post(`/api/designs`, jsonBody);
  if (!response.ok) return -1
  return response.body.designId;
}

/** Shared across duplicate DatabasePanel mounts so first login only creates one design. */
let ensureAtLeastOneDesignPromise: Promise<number> | null = null;

export async function ensureAtLeastOneDesign(api: any, templateName: string): Promise<number> {
  if (ensureAtLeastOneDesignPromise) return ensureAtLeastOneDesignPromise;

  ensureAtLeastOneDesignPromise = (async () => {
    const existing = await fetchUserDesigns(
      api,
      new PaginatedSearchRequestDto({ pageNumber: 1, pageSize: 1 })
    );
    if (existing?.data && existing.data.length > 0) {
      return existing.data[0].designId;
    }
    return getNewDesign(api, templateName);
  })();

  try {
    return await ensureAtLeastOneDesignPromise;
  } finally {
    ensureAtLeastOneDesignPromise = null;
  }
}

export async function removeDesign(api: any, designId: number): Promise<boolean> {
  try {
    const response = await api.delete(`/api/designs/${designId}`);
    if (response.ok) {
      console.log(`design ${designId} removed successfuly.`);
      return true;
    }
    else return false;

  } catch (error) {
    return false;
  }
}

let templateLoadPendingClose = false;
let designLoadPendingClose = false;

export function consumeTemplateLoadPendingClose(): boolean {
  if (!templateLoadPendingClose) return false;
  templateLoadPendingClose = false;
  return true;
}

export function markDesignLoadPendingClose() {
  designLoadPendingClose = true;
}

export function consumeDesignLoadPendingClose(): boolean {
  if (!designLoadPendingClose) return false;
  designLoadPendingClose = false;
  return true;
}

export async function loadPrivateTemplate(api: any, templateId: number): Promise<number> {
  const currentDesignId = useSelectedDesignStore.getState().designId;
  const currentDesign = useDesignStore.getState().insoleDesign;
  const currentEngraving = useDesignStore.getState().engraving;
  if (currentDesign?.designType === DesignType.General) {
    useSelectedDesignStore.setState({ previousDesignId: currentDesignId });
    useDesignStore.setState({
      previousDesign: InsoleDesign.cloneFrom(currentDesign),
      previousEngraving: currentEngraving ? Engraving.cloneFrom(currentEngraving) : currentEngraving,
    });
  }

  const response = await api.get(`/api/templates/${templateId}`);
  if (!response.ok) return -1;
  const designId = response.body.designId;
  templateLoadPendingClose = true;
  useSelectedDesignStore.getState().setDesignId(designId);
  return designId;
}

export async function loadAdditionsFromList(api: any, insole: Insole, additionsList: any) {
  for (const item of additionsList) {
    const additionMeshResponse = await api.get(`/api/additions/${item.additionId}/mesh`);
    if (additionMeshResponse.body) {
      const addition = new Addition();
      addition.loadFromJson(item, useDesignStore.getState().insoleDesign?.category);
      const geometry = loadFromArrayBuffer(additionMeshResponse.body);
      if (geometry) {
        addition.loadAdditionFromBufferGeo(geometry);
        insole.additions.push(addition);
      }
    }
  }
  return insole;
}

export async function saveDesignAsTemplate(api: any, designId: number) {
  const res = await api.post(`/api/templates/${designId}`);
  if (res.ok) console.log('save template successfuly');
}

export async function base64ToUint8Array(base64: string): Promise<ArrayBuffer> {
  const res = await fetch(`data:application/octet-stream;base64,${base64}`);
  const buffer = await res.arrayBuffer();
  return buffer;
}

export async function getTemplate(api: any, templateName: string): Promise<InsoleDesign | null> {
  const name = resolveInsoleTemplateApiName(templateName);
  if (!name) return null;
  const response = await api.get(`/api/templates/byname?templateName=${encodeURIComponent(name)}`);
  if (!response.ok) return null;
  const designId = response.body.designId;
  const [design, patient] = await loadDesign(api, designId);
  return design?.insoles?.length ? design : null;
}

export async function replaceAdditons(api: any, design: InsoleDesign, designId: number) {
  var jsonData: any[] = [];
  design.insoles.forEach((insole, side) => {
    insole.additions.forEach((addition, index) => {
      jsonData.push(addition.toJSON());
    });
  });
  if (jsonData.length === 0) {
    updateAutoPropertiesAfterReplace(design, new AutoProperties());
    //return;
  }
  const payload = {
    "additions": jsonData
  };
  const response = await api.post(`/api/designs/${designId}/additions/bulk`, payload);
  if (response.ok) {

    const additionsRes = response.body.returnAdditionDto;
    if (additionsRes.length < 1) return;
    const rightAdditions = additionsRes.filter((item: any) => String(item.side).toLowerCase() === 'right');
    const leftAdditions = additionsRes.filter((item: any) => String(item.side).toLowerCase() === 'left');
    rightAdditions.forEach((addi: any, index: number) => {
      design.insoles[0].additions[index].designAdditionId = parseInt(addi.designAdditionId);
    });
    leftAdditions.forEach((addi: any, index: number) => {
      design.insoles[1].additions[index].designAdditionId = parseInt(addi.designAdditionId);
    });

    // updateAutoPropertiesAfterReplace(design, response.body.autoProperties);

  }
}

export async function getDesignPressureAlignments(api: any, designId: number) {
  try {
    const response = await api.get(`/api/designs/${designId}/pressure`);
    if (!response.ok) { return null; }
    var dto = new GetDesignPressureResponseDto(response.body);
    return dto;
  } catch (error) {
    console.error(`error loading presure alignments: ${error}`);
    return null;
  }
}

export async function saveDesignPressureAlignment(
  api: any, designId: number, alignPressureDto: DesignPressureAlignmentRequestDto
) {
  const response = await api.patch(`/api/designs/${designId}/pressure/alignment`, alignPressureDto);
  return response.ok;
}

function updateAutoPropertiesAfterReplace(design: InsoleDesign, autoProperties: any) {
  if (design === null || design === undefined || design.autoProperties === null) return;
  design.autoProperties.cutoutMet1Right = autoProperties.cutoutMet1Right;
  design.autoProperties.cutoutMet2Right = autoProperties.cutoutMet2Right;
  design.autoProperties.cutoutMet3Right = autoProperties.cutoutMet3Right;
  design.autoProperties.cutoutMet4Right = autoProperties.cutoutMet4Right;
  design.autoProperties.cutoutMet5Right = autoProperties.cutoutMet5Right;

  design.autoProperties.dancersPadRight = autoProperties.dancersPadRight;
  design.autoProperties.metatarSalBarRight = autoProperties.metatarSalBarRight;
  design.autoProperties.metatarSalPadRight = autoProperties.metatarSalPadRight;
  design.autoProperties.mortonsExtensionRight = autoProperties.mortonsExtensionRight;

  design.autoProperties.cutoutMet1Left = autoProperties.cutoutMet1Left;
  design.autoProperties.cutoutMet2Left = autoProperties.cutoutMet2Left;
  design.autoProperties.cutoutMet3Left = autoProperties.cutoutMet3Left;
  design.autoProperties.cutoutMet4Left = autoProperties.cutoutMet4Left;
  design.autoProperties.cutoutMet5Left = autoProperties.cutoutMet5Left;

  design.autoProperties.dancersPadLeft = autoProperties.dancersPadLeft;
  design.autoProperties.metatarSalBarLeft = autoProperties.metatarSalBarLeft;
  design.autoProperties.metatarSalPadLeft = autoProperties.metatarSalPadLeft;
  design.autoProperties.mortonsExtensionLeft = autoProperties.mortonsExtensionLeft;

  design.autoProperties.insoleTemplate = autoProperties.insoleTemplate;
}

export async function SavePatientOnServer(api: any, patient: Patient, designId: number) {
  const res = await api.put(`/api/patients/${designId}`, patient.toJson());
  if (res.ok) return true;
  return false;
}

export async function getPressureCalib(api: any, userId: number): Promise<Calibrator | null> {
  let res = null;
  try {
    res = await api.get(`/api/user/${userId}/pressure_calibration/true`);
    if (!res.ok) return null;
  } catch (error) {
    console.error('loading pressure calibration files faild.');
    return null;
  }

  const zipArray = await base64ToUint8Array(res.body.data);
  const zip = await JSZip.loadAsync(zipArray);

  const calFiles: { name: string, content: string }[] = [];
  let txtFile: string | null = null;

  for (const fileName of Object.keys(zip.files)) {
    const file = zip.file(fileName);
    if (!file) continue;

    if (fileName.endsWith(".cal")) {
      const content = await file.async("text");
      calFiles.push({ name: fileName, content: content });
    } else if (fileName.endsWith(".txt")) {
      txtFile = await file.async("text");
    }
  }

  calFiles.sort((a, b) => {
    const numA = parseFloat(a.name.replace('.cal', ''));
    const numB = parseFloat(b.name.replace('.cal', ''));
    return numA - numB;
  });

  const pressureCalibrator = new Calibrator();
  pressureCalibrator.Load(txtFile, calFiles);
  //const matrictest :Point[][][] | null = pressureCalibrator.calibrationMatrix;
  //downloadOpenCVPointMatrixExcel(matrictest , "test");
  return pressureCalibrator;
}

type Pad = {
  id: number;
  name: string;
  groupName: string;
  hardness: string;
  iMode: string;
  isReverse: boolean;
  centerIndex: number;
  scale: { x: number; y: number; z: number };
  height: number;
  angle: number;
  relativeLocation: { x: number; y: number; z: number };
};

function formatGroups(pads: Pad[]): additionNameGroup[] {
  const groups: Record<string, string[]> = {};

  // group by groupName
  for (const pad of pads) {
    if (!groups[pad.groupName]) {
      groups[pad.groupName] = [];
    }
    groups[pad.groupName].push(pad.name);
  }

  // convert to required output format
  return Object.entries(groups).map(([group, fileNames]) => ({
    group,
    fileNames
  }));
}

export async function getAllAdditions(api: any): Promise<additionNameGroup[] | null> {
  const serverAddditions = await api.get('/api/additions/?pageNumber=1&pageSize=100');
  if (!serverAddditions.ok) return null;
  const pads: Pad[] = serverAddditions.body;
  const groupedAdditions = formatGroups(pads);
  return groupedAdditions;
}

export async function ApplyGcodeSettingFromTemplate(api: any, templateId: number, designId: number) {
  const formData = new FormData();
  formData.append("templateId", templateId.toString());
  formData.append("designId", designId.toString());
  const res = await api.post('/api/gsetting/clone', formData);
  if (res.ok) { }
}

export async function GetSavedGcodeSetting(
  api: any,
  designId: number
): Promise<GCodeSetting[]> {
  let defaultSettings = [];
  defaultSettings.push(getDefaultGcodeSetting('right', designId));
  defaultSettings.push(getDefaultGcodeSetting('left', designId));
  try {
    const res = await api.get(`/api/gsetting/${designId}`);
    // if (!Array.isArray(res?.body)) return [];
    if (!res.ok) {
      return defaultSettings;
    }

    return res.body.map((item: any): GCodeSetting => {
      if (item.insoleHardness == null) {
        return item;
      }

      return {
        ...item,
        foreFootHardness: item.insoleHardness,
        midInnerHardness: item.insoleHardness,
        midOuterHardness: item.insoleHardness,
        heelHardness: item.insoleHardness,
      };
    });

  } catch (error) {
    console.error("GetSavedGcodeSetting error:", error);
    return defaultSettings;
  }
}


// export function downloadOpenCVPointMatrixExcel(
//   matrix: Point[][][] | null,
//   filePrefix: string
// ): void {
//   if (matrix === null) return;
//   const width = matrix.length;
//   const height = matrix[0]?.length ?? 0;
//   const depth = matrix[0]?.[0]?.length ?? 0;

//   const workbookX = XLSX.utils.book_new();
//   const workbookY = XLSX.utils.book_new();

//   for (let layer = 0; layer < depth; layer++) {
//     const sheetX: number[][] = [];
//     const sheetY: number[][] = [];

//     for (let w = 0; w < width; w++) {
//       sheetX[w] = [];
//       sheetY[w] = [];
//       for (let h = 0; h < height; h++) {
//         const pt = matrix[w][h][layer];
//         sheetX[w][h] = pt.x;
//         sheetY[w][h] = pt.y;
//       }
//     }

//     XLSX.utils.book_append_sheet(
//       workbookX,
//       XLSX.utils.aoa_to_sheet(sheetX),
//       `Layer_${layer}`
//     );
//     XLSX.utils.book_append_sheet(
//       workbookY,
//       XLSX.utils.aoa_to_sheet(sheetY),
//       `Layer_${layer}`
//     );
//   }

//   const wboutX = XLSX.write(workbookX, { bookType: "xlsx", type: "array" });
//   const wboutY = XLSX.write(workbookY, { bookType: "xlsx", type: "array" });

//   const blobX = new Blob([wboutX], { type: "application/octet-stream" });
//   const blobY = new Blob([wboutY], { type: "application/octet-stream" });

//   const linkX = document.createElement("a");
//   const linkY = document.createElement("a");

//   linkX.href = URL.createObjectURL(blobX);
//   linkY.href = URL.createObjectURL(blobY);

//   linkX.download = `${filePrefix}_x.xlsx`;
//   linkY.download = `${filePrefix}_y.xlsx`;

//   document.body.appendChild(linkX);
//   document.body.appendChild(linkY);

//   linkX.click();
//   linkY.click();

//   document.body.removeChild(linkX);
//   document.body.removeChild(linkY);

//   URL.revokeObjectURL(linkX.href);
//   URL.revokeObjectURL(linkY.href);
// }

export async function fetchUserDesigns(
  api: any, paginatedRequestDto: PaginatedSearchRequestDto
): Promise<PaginatedResponseDto<ApiDesignData> | undefined> {
  const res = await api.get(`/api/designs?${paginatedRequestDto.toQueryString()}`);
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return;
  }
  return res?.body;
}

export async function updateDesignPrintCount(api: any, designId: number, printCount: number) {
  const updatePrintCountResponse = await api.patch(`/api/designs/${designId}`, { printCount: printCount });
  if (updatePrintCountResponse.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  }
}

export async function cloneDesignByDesignId(api: any, designId: number): Promise<number | null> {
  const res = await api.post(`/api/designs/clone`, { designId: designId });
  if (res.status === 401) {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    return null;
  } else if (res.ok) {
    return res.body.designId;
  }
  return null;
}

export const addNewAddition = async (api: any, additionName: string, foot: number): Promise<Addition | null> => {
  const side = foot === 0 ? "right" : "left";
  const addition = await getNewAddition(api, additionName, side);

  if (!addition) {
    return null;
  }

  addition.status = AdditionNewStatus.isNew;
  return addition;
}

function loadPressureFromPreviousDesign(design: InsoleDesign) {
  const previosDesign = useDesignStore.getState().previousDesign;
  if (previosDesign)
    if (previosDesign.pressureData) {
      design.pressureData = PressureData.cloneFrom(previosDesign?.pressureData);
    }
}

function loadWBCTFromPreviousDesign(design: InsoleDesign) {
  const previosDesign = useDesignStore.getState().previousDesign;
  if (previosDesign) {
    for (let i = 0; i < previosDesign.insoles.length; i++) {
      const bonesGroup = previosDesign.insoles[i]?.bonesGroup;

      if (bonesGroup && bonesGroup instanceof THREE.Group) {
        design.insoles[i].bonesGroup = bonesGroup.clone();
      }
      else {
        design.insoles[i].bonesGroup = null;
      }

      const bonesMarkers = previosDesign.insoles[i]?.bonesMarkers;
      if (bonesMarkers && bonesMarkers instanceof THREE.Group) {
        design.insoles[i].bonesMarkers = bonesMarkers.clone();
      } else {
        design.insoles[i].bonesMarkers = null;
      }

      const bonesFPIMarkers = previosDesign.insoles[i]?.bonesFPIMarkers;
      if (bonesFPIMarkers && bonesFPIMarkers instanceof THREE.Group) {
        design.insoles[i].bonesFPIMarkers = bonesFPIMarkers.clone();
      } else {
        design.insoles[i].bonesFPIMarkers = null;
      }

      const curvePoints = previosDesign.insoles[i]?.curvePoints;
      if (curvePoints instanceof THREE.Group && curvePoints.children.length > 0) {
        design.insoles[i].curvePoints = curvePoints.clone();
      } else {
        design.insoles[i].curvePoints = null;
      }

      const footShoeMesh = previosDesign.insoles[i]?.footShoeMesh;
      if (footShoeMesh && footShoeMesh instanceof THREE.Mesh) {
        design.insoles[i].footShoeMesh = footShoeMesh.clone();
      } else {
        design.insoles[i].footShoeMesh = null;
      }

      const shoeInsideMarkers = previosDesign.insoles[i]?.shoeInsideMarkers;
      if (shoeInsideMarkers && shoeInsideMarkers.length > 0) {
        design.insoles[i].shoeInsideMarkers = shoeInsideMarkers.map(point => point.clone());
      } else {
        design.insoles[i].shoeInsideMarkers = [];
      }

      const shoeOutsideMarkers = previosDesign.insoles[i]?.shoeOutsideMarkers;
      if (shoeOutsideMarkers && shoeOutsideMarkers.length > 0) {
        design.insoles[i].shoeOutsideMarkers = shoeOutsideMarkers.map(point => point.clone());
      } else {
        design.insoles[i].shoeOutsideMarkers = [];
      }

    }
  }
}
